/**
 * Cancel-intent exit interview (MONETIZATION_STRATEGY_HORMOZI.md §9 item L).
 *
 * The identifiers MUST stay byte-identical to `CANCELLATION_REASONS` in
 * `app-backend/src/modules/billing/cancellation-feedback.types.ts` — the
 * backend validates against a closed allowlist and answers a mismatched value
 * with a 400. The English lives here only; the backend deliberately ships no
 * merchant-facing copy so wording has exactly one home.
 */

export const CANCELLATION_REASONS = [
  'too_expensive',
  'not_enough_value',
  'quality_not_good_enough',
  'too_hard_to_use',
  'missing_features',
  'switching_to_alternative',
  'store_closing_or_paused',
  'temporary_pause',
  'other',
] as const;

export type CancellationReason = (typeof CANCELLATION_REASONS)[number];

/** Matches the server-side cap; the server truncates rather than rejecting. */
export const CANCELLATION_DETAIL_MAX_LENGTH = 1000;

export interface CancellationFeedbackPayload {
  reason: CancellationReason;
  detail?: string;
}

export interface CancellationFeedbackResult {
  recorded: boolean;
  updated: boolean;
}

/**
 * Ordered for the merchant, not for us: the reasons we can actually act on
 * come first, and the two "nothing is wrong, my situation changed" answers sit
 * near the bottom so a merchant pausing their store is not nudged into telling
 * us the product was bad.
 */
export const CANCELLATION_REASON_LABELS: Record<CancellationReason, string> = {
  too_expensive: "It costs more than it's worth to me",
  not_enough_value: "I'm not seeing enough results from AR",
  quality_not_good_enough: "The 3D models were not good enough",
  too_hard_to_use: 'It was too hard to set up or use',
  missing_features: 'It is missing something I need',
  switching_to_alternative: "I'm switching to another app",
  store_closing_or_paused: 'My store is closing or on hold',
  temporary_pause: 'Just pausing — I plan to come back',
  other: 'Something else',
};
